import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import clsx from 'clsx';
import { useAuthStore } from '@/stores/auth';
import { useUIStore } from '@/stores/ui';
import Avatar from '@/components/ui/Avatar';
import {
  Bars3Icon,
  BellIcon,
  MagnifyingGlassIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';

export default function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { sidebarCollapsed, setSidebarOpen, theme, setTheme, notifications } = useUIStore();
  const [searchOpen, setSearchOpen] = useState(false);

  const unreadNotifications = notifications.filter((n) => !n.read).length;
  const isDark = theme === 'dark';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  return (
    <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between px-4 lg:px-6">
      {/* Left section - Logo for collapsed state + Hamburger */}
      <div className="flex items-center gap-4">
        {/* Mobile hamburger */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <Bars3Icon className="w-6 h-6" />
        </button>

        {/* Logo - shows on mobile always or when sidebar collapsed on desktop */}
        <div className={clsx(
          'flex items-center gap-2',
          // On mobile: always show
          'flex',
          // On desktop: only show when sidebar is collapsed
          sidebarCollapsed ? 'lg:flex' : 'lg:hidden'
        )}>
          <img 
            src="/logo.png" 
            alt="Kampus Media" 
            className="h-7 object-contain dark:invert"
          />
        </div>
      </div>

      {/* Center - Search (Desktop) */}
      <div className="hidden md:flex flex-1 max-w-md mx-4">
        <div className="relative w-full">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search..."
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900 focus:bg-white dark:focus:bg-slate-800 transition-all"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-1 px-2 py-0.5 text-xs text-slate-400 bg-slate-100 dark:bg-slate-700 rounded-md">
            ⌘K
          </kbd>
        </div>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-2">
        {/* Mobile search */}
        <button
          onClick={() => setSearchOpen(!searchOpen)}
          className="md:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <MagnifyingGlassIcon className="w-5 h-5" />
        </button>

        {/* Theme Toggle - Simple switch */}
        <button
          onClick={toggleTheme}
          className={clsx(
            'relative p-2 rounded-xl transition-all duration-300',
            isDark 
              ? 'bg-slate-800 text-primary-400 hover:bg-slate-700' 
              : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
          )}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <div className="relative w-5 h-5">
            <SunIcon 
              className={clsx(
                'absolute inset-0 w-5 h-5 transition-all duration-300',
                isDark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
              )} 
            />
            <MoonIcon 
              className={clsx(
                'absolute inset-0 w-5 h-5 transition-all duration-300',
                isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
              )} 
            />
          </div>
        </button>

        {/* Notifications */}
        <Menu as="div" className="relative">
          <Menu.Button className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <BellIcon className="w-5 h-5" />
            {unreadNotifications > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-danger-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-900 rounded-xl shadow-dropdown border border-slate-100 dark:border-slate-800 overflow-hidden z-50">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <h3 className="font-semibold text-slate-800 dark:text-slate-200">Notifications</h3>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-slate-500">
                    <BellIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No notifications yet</p>
                  </div>
                ) : (
                  notifications.slice(0, 5).map((notification) => (
                    <div
                      key={notification.id}
                      className={clsx(
                        'px-4 py-3 border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors',
                        !notification.read && 'bg-primary-50/50 dark:bg-primary-900/20'
                      )}
                    >
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{notification.title}</p>
                      {notification.message && (
                        <p className="text-sm text-slate-500 mt-0.5">{notification.message}</p>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800">
                <Link
                  to="/notifications"
                  className="text-sm text-primary-500 font-medium hover:text-primary-600"
                >
                  View all notifications
                </Link>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>

        {/* User Menu */}
        <Menu as="div" className="relative ml-2">
          <Menu.Button className="flex items-center gap-3 p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <Avatar name={user?.username || 'User'} size="sm" status="online" />
            <div className="hidden lg:block text-left">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{user?.username || 'User'}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role || 'admin'}</p>
            </div>
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900 rounded-xl shadow-dropdown border border-slate-100 dark:border-slate-800 py-1 z-50">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{user?.username}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/profile"
                    className={clsx(
                      'flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-700 dark:text-slate-300',
                      active && 'bg-slate-50 dark:bg-slate-800'
                    )}
                  >
                    <UserCircleIcon className="w-4 h-4" />
                    My Profile
                  </Link>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/admin/settings"
                    className={clsx(
                      'flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-700 dark:text-slate-300',
                      active && 'bg-slate-50 dark:bg-slate-800'
                    )}
                  >
                    <Cog6ToothIcon className="w-4 h-4" />
                    Settings
                  </Link>
                )}
              </Menu.Item>
              <div className="my-1 border-t border-slate-100 dark:border-slate-800" />
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleLogout}
                    className={clsx(
                      'flex items-center gap-2 w-full px-4 py-2 text-sm text-danger-600',
                      active && 'bg-slate-50 dark:bg-slate-800'
                    )}
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    Sign out
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </header>
  );
}
